import {useState, type FormEvent} from 'react'
import type {AxiosError} from 'axios'
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {PorQue} from '@/components/ui/por-que'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Skeleton} from '@/components/ui/skeleton'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  useDecidirIngreso,
  usePreviewIngreso,
  type ChequeoIngreso,
  type FilaCongelada,
} from '@/hooks/empresa/obras/use-ingreso'
import {translateApiError} from '@/lib/errors'
import {formatInstantDate, formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const TONO_POR_RESULTADO: Record<string, 'green' | 'amber' | 'red' | 'neutral'> = {
  cumple: 'green',
  aviso: 'amber',
  falla: 'red',
  no_verificable: 'neutral',
}

// Dudas sobre un dato de salud: van con relieve y no en el gris de «no pude verificar».
const DESTACADOS = new Set(['restriccionSinEmparejar', 'restriccionesSinTranscribir'])

const TONO_POR_COLOR: Record<string, 'green' | 'amber' | 'red' | 'enCurso' | 'neutral'> = {
  verde: 'green',
  amarillo: 'amber',
  rojo: 'red',
  en_curso: 'enCurso',
  gris: 'neutral',
}

/** Solo dos salidas; con algún rojo, autorizar exige motivo. Lo que se ve es lo que la constancia congela (E10-H12). */
export function DecisionIngresoPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {businessId, proyectoId, employeeId} = useParams<{
    businessId: string
    proyectoId: string
    employeeId: string
  }>()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const [busqueda] = useSearchParams()
  const previo = usePreviewIngreso(business ? businessId : undefined, employeeId, proyectoId)
  const decidir = useDecidirIngreso(businessId!, proyectoId)
  const [motivo, setMotivo] = useState('')
  const [errorMotivo, setErrorMotivo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!businessId || !proyectoId || !employeeId) return null
  if (guard) return guard
  if (capabilities.plan_tier === 'free') {
    return <PlanLockedPage feature={t('plans.feature.obras')} businessId={businessId} businessName={business!.name} />
  }
  // Se vuelve a la lista tal como estaba: pestaña, filtro y página.
  const guardado = busqueda.get('volver') ?? ''
  const cola = guardado.includes('tab=') ? guardado : `${guardado ? `${guardado}&` : ''}tab=ingreso`
  const volver = `/businesses/${businessId}/obras/${proyectoId}?${cola}`
  if (previo.isError) {
    const status = (previo.error as AxiosError)?.response?.status
    const recuperable = status !== 403 && status !== 404
    return (
      <div className="mx-auto max-w-2xl space-y-3">
        <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="ingreso-error-carga">
          {status === 403
            ? t('ingreso.errorSinFirma')
            : status === 404
              ? t('ingreso.errorNoExiste')
              : t('ingreso.errorCarga')}
        </p>
        <div className="flex items-center gap-3">
          {recuperable && (
            <Button size="sm" variant="outline" onClick={() => void previo.refetch()} data-testid="ingreso-reintentar">
              {t('ingreso.reintentar')}
            </Button>
          )}
          <Link
            to={volver}
            className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase hover:underline">
            {t('ingreso.volver')}
          </Link>
        </div>
      </div>
    )
  }
  if (!previo.data) return <Skeleton className="h-64 w-full" />

  const datos = previo.data
  // `exige_motivo` cuenta filas rojas y fallas de chequeo: contar solo las filas decía «0 en rojo» en el peor caso.
  const fallas = datos.chequeos.filter((chequeo) => chequeo.resultado === 'falla')
  const rojos = datos.impide.length + fallas.length
  // Puerta de firmante, no solo el cinturón de modo dios: sin `sgsst_firmar` el último clic daría 403.
  const puedeDecidir = capabilities.sgsst_firmar && !godReadOnly
  const razonBloqueo = godReadOnly ? t('ingreso.bloqueoModoDios') : capabilities.firmar_bloqueado_por

  async function decidirCon(autorizado: boolean, event: FormEvent) {
    event.preventDefault()
    setError(null)
    const falta = datos.exige_motivo && autorizado ? validateRequired(motivo) : null
    setErrorMotivo(falta)
    if (falta) return
    try {
      await decidir.mutateAsync({
        employeeId: employeeId!,
        autorizado,
        motivo: motivo.trim() || null,
        decisionToken: datos.decision_token,
      })
      navigate(volver)
    } catch (problema) {
      setError(translateApiError(problema))
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="decision-ingreso">
      <Link to={volver} className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase hover:underline">
        {t('ingreso.volver')}
      </Link>
      <PageHeading
        title={datos.nombre}
        subtitle={t('ingreso.subtitulo', {
          cedula: datos.cedula,
          obra: datos.proyecto_nombre ?? t('ingreso.sinObra'),
        })}
      />

      <div className="surface space-y-2 rounded-lg px-4 py-3" data-testid="ingreso-franja">
        <div className="flex flex-wrap items-center gap-2">
          {/* No puede decir «Habilitada» en verde mientras un chequeo falla. */}
          <Tag
            tone={datos.veredicto === 'habilitada' && !datos.exige_motivo ? 'green' : 'red'}
            data-testid="ingreso-veredicto">
            {t(`ingreso.veredicto.${datos.veredicto}`)}
          </Tag>
          {/* La misma clave que la ficha (P-E10-1): las dos superficies no redactan distinto la misma auditoría. */}
          <PorQue
            label={t('common.porQue')}
            ariaLabel={`${t('common.porQue')} — ${t(`ingreso.veredicto.${datos.veredicto}`)}`}
            testId="ingreso-criterio"
            contenidoTestId="ingreso-criterio-contenido">
            <p>
              {t(`cumplimiento.criterio.${datos.criterio_origen}`, {
                fecha: datos.criterio_fijada_en ? formatDateOnly(datos.criterio_fijada_en) : '',
                nombre: datos.criterio_fijada_por ?? '',
              })}
            </p>
          </PorQue>
        </div>
        <p className="text-sm text-foreground" data-testid="ingreso-no-decide">
          {t('ingreso.franja.noDecide')}
        </p>
        {datos.ultima && (
          <div className="space-y-1 text-xs text-muted-foreground" data-testid="ingreso-ultima">
            <p>
              {t('ingreso.ultimaDecision', {
                fecha: formatInstantDate(datos.ultima.decidido_en),
                nombre: datos.ultima.decidido_por_nombre ?? '',
                veredicto: t(`ingreso.ultimaVeredicto.${datos.ultima.autorizado}`),
              })}
            </p>
            <PorQue label={t('common.porQue')} testId="ingreso-ultima-porque">
              <p>{t('ingreso.ultimaDecisionPorque')}</p>
            </PorQue>
          </div>
        )}
      </div>

      <Bloque titulo={t('ingreso.impide')} testid="ingreso-impide" vacio={t('ingreso.sinRojos')}>
        {datos.impide.map((fila) => (
          <FilaLeida key={fila.requisito_id} fila={fila} />
        ))}
      </Bloque>
      {/* Los rojos del estándar mínimo no impiden (§2): se cuentan aparte, no bajo «Lo que impide». */}
      {datos.rojos_no_deshabilitantes > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="ingreso-rojos-no-deshabilitantes">
          {t('cumplimiento.franja.rojosNoDeshabilitantes', {count: datos.rojos_no_deshabilitantes})}
        </p>
      )}

      <Bloque titulo={t('ingreso.porVencer')} testid="ingreso-por-vencer" vacio={t('ingreso.sinAmarillos')}>
        {datos.por_vencer.map((fila) => (
          <FilaLeida key={fila.requisito_id} fila={fila} />
        ))}
      </Bloque>

      <section className="space-y-2" data-testid="ingreso-chequeos">
        <h2 className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase">{t('ingreso.chequeos')}</h2>
        <ul className="space-y-2">
          {datos.chequeos.map((chequeo) => (
            <ChequeoLeido key={chequeo.numero} chequeo={chequeo} businessId={businessId} employeeId={employeeId} />
          ))}
        </ul>
      </section>

      {datos.no_verificado.length > 0 && (
        <section className="space-y-1" data-testid="ingreso-no-verificado">
          <h2 className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase">
            {t('ingreso.noVerificado')}
          </h2>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {datos.no_verificado.map((linea, indice) => (
              <li key={`${linea.motivo}-${indice}`}>
                {t(`cumplimiento.noVerificable.${linea.motivo}`, {
                  nombre: linea.nombre,
                  fecha: linea.fecha ? formatDateOnly(linea.fecha) : '',
                  count: Number(linea.nombre) || 0,
                })}
              </li>
            ))}
          </ul>
        </section>
      )}

      <form className="space-y-3" noValidate onSubmit={(event) => void decidirCon(true, event)}>
        {datos.exige_motivo && (
          <div className="space-y-1">
            <Label htmlFor="ingreso_motivo">{t('ingreso.motivo.label')}</Label>
            <div className="space-y-1" data-testid="ingreso-que-se-deja-pasar">
              <p className="text-xs text-muted-foreground">{t('ingreso.queSeDejaPasar', {count: rojos})}</p>
              <ul className="text-xs text-muted-foreground">
                {datos.impide.map((fila) => (
                  <li key={fila.requisito_id}>
                    {fila.nombre} — {t(`ingreso.frase.${fila.frase}`)}
                  </li>
                ))}
                {fallas.map((chequeo) => (
                  <li key={chequeo.numero}>
                    {t(`ingreso.chequeo.${chequeo.numero}`)} —{' '}
                    {t(`ingreso.chequeoMotivo.${chequeo.motivo}`, {
                      uno: chequeo.detalle[0] ?? '',
                      dos: chequeo.detalle[1] ?? '',
                      lista: chequeo.detalle.join(', '),
                    })}
                  </li>
                ))}
              </ul>
            </div>
            <Textarea
              id="ingreso_motivo"
              value={motivo}
              onChange={(event) => setMotivo(event.target.value)}
              onBlur={() => setErrorMotivo(validateRequired(motivo))}
              maxLength={tope('NOTA')}
              data-testid="ingreso-motivo"
            />
            {errorMotivo && (
              <p className="text-destructive text-xs" data-testid="ingreso-motivo-error">
                {t(errorMotivo)}
              </p>
            )}
          </div>
        )}

        {!puedeDecidir && razonBloqueo && (
          <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="ingreso-bloqueo">
            {razonBloqueo}
          </p>
        )}
        {error && (
          <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="ingreso-error">
            {error}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={!puedeDecidir || decidir.isPending} data-testid="ingreso-autorizar">
            {t('ingreso.autorizar')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!puedeDecidir || decidir.isPending}
            onClick={(event) => void decidirCon(false, event)}
            data-testid="ingreso-no-autorizar">
            {t('ingreso.noAutorizar')}
          </Button>
        </div>
      </form>
    </div>
  )
}

function Bloque({
  titulo,
  testid,
  vacio,
  children,
}: {
  titulo: string
  testid: string
  vacio: string
  children: React.ReactNode[]
}) {
  return (
    <section className="space-y-2" data-testid={testid}>
      <h2 className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase">{titulo}</h2>
      {children.length === 0 ? (
        <p className="text-sm text-muted-foreground">{vacio}</p>
      ) : (
        <ul className="space-y-2">{children}</ul>
      )}
    </section>
  )
}

function FilaLeida({fila}: {fila: FilaCongelada}) {
  const {t} = useTranslation()
  return (
    <li className="surface flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-2">
      <span className="text-sm text-foreground">{fila.nombre}</span>
      <Tag tone={fila.color ? TONO_POR_COLOR[fila.color] : 'neutral'}>{t(`ingreso.frase.${fila.frase}`)}</Tag>
    </li>
  )
}

function ChequeoLeido({
  chequeo,
  businessId,
  employeeId,
}: {
  chequeo: ChequeoIngreso
  businessId: string
  employeeId: string
}) {
  const {t} = useTranslation()
  return (
    <li
      className="surface flex flex-wrap items-start justify-between gap-2 rounded-lg px-4 py-2"
      data-testid={`ingreso-chequeo-${chequeo.numero}`}>
      <div className="space-y-1">
        <p className="text-sm text-foreground">{t(`ingreso.chequeo.${chequeo.numero}`)}</p>
        <p
          className={
            DESTACADOS.has(chequeo.motivo)
              ? 'notice-amber rounded-lg px-2 py-1 text-xs'
              : 'text-xs text-muted-foreground'
          }>
          {t(`ingreso.chequeoMotivo.${chequeo.motivo}`, {
            uno: chequeo.detalle[0] ?? '',
            dos: chequeo.detalle[1] ?? '',
            lista: chequeo.detalle.join(', '),
          })}
        </p>
        {chequeo.numero === 2 && chequeo.detalle.length >= 2 && (
          <div className="flex flex-wrap gap-x-6 gap-y-1" data-testid="ingreso-chequeo-2-cargos">
            <p className="text-xs">
              <span className="font-mono text-[10px] tracking-wide text-muted-foreground uppercase">
                {t('ingreso.chequeo2.enElCertificado')}
              </span>{' '}
              {chequeo.detalle[0]}
            </p>
            <p className="text-xs">
              <span className="font-mono text-[10px] tracking-wide text-muted-foreground uppercase">
                {t('ingreso.chequeo2.enPlanta')}
              </span>{' '}
              {chequeo.detalle[1]}
            </p>
          </div>
        )}
        {chequeo.motivo === 'sinCargoLeido' && (
          <Link
            to={`/businesses/${businessId}/empleados/${employeeId}?tab=examenes`}
            className="text-xs underline"
            data-testid="ingreso-chequeo-2-examen">
            {t('ingreso.chequeo2.verExamen')}
          </Link>
        )}
      </div>
      <Tag tone={TONO_POR_RESULTADO[chequeo.resultado]}>{t(`ingreso.resultado.${chequeo.resultado}`)}</Tag>
    </li>
  )
}
